import { sentryRootOptions } from './config/sentry';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { store } from './store/store';
import { Provider } from 'react-redux';
import CustomThemeProvider from './theme/CustomThemeProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClient';
import { BrowserRouter } from 'react-router-dom';
import './i18n';
import { initAnalytics } from './analytics/nodeAnalytics';
import { registerServiceWorker } from './serviceWorkerRegistration';
import OfflineBoundary from './offline/OfflineBoundary';
import { isChunkLoadError, reloadAfterChunkError } from './utils/chunkLoadRecovery';

// Patch DOM methods to handle external DOM manipulation (antivirus scanners, browser extensions)
// React throws "removeChild"/"insertBefore" errors when external tools modify the DOM.
// See: https://github.com/facebook/react/issues/17256
if (typeof Node !== 'undefined' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      console.warn('removeChild: node is not a child of this node — ignored (likely external DOM mutation)');
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn('insertBefore: reference node is not a child of this node — ignored (likely external DOM mutation)');
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

window.addEventListener('error', (event) => {
  // A chunk removed by a newer deploy is answered with index.html by the
  // hosting rewrite, so the stale tab hits a SyntaxError instead of ChunkLoadError.
  const isStaleChunkHtml =
    event.message?.includes("Unexpected token '<'") && event.filename?.includes('/static/js/');
  if (isStaleChunkHtml || isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
    if (reloadAfterChunkError()) {
      event.preventDefault();
    }
  }
  // Suppress DOM mutation errors from external tools (antivirus, extensions)
  if (event.message?.includes('removeChild') || event.message?.includes('insertBefore')) {
    console.warn('External DOM mutation error suppressed');
    event.preventDefault();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (isChunkLoadError(reason)) {
    if (reloadAfterChunkError()) {
      event.preventDefault();
    }
  }
});

// Initialize analytics SDK with client version
initAnalytics({ clientVersion: process.env.REACT_APP_VERSION || '0.1.0' });

const root = ReactDOM.createRoot(document.getElementById('root'), sentryRootOptions);

root.render(
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <CustomThemeProvider>
          <OfflineBoundary><App /></OfflineBoundary>
        </CustomThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </Provider>
);

reportWebVitals();
registerServiceWorker();
